import React, { useState, useEffect, useRef } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, Checkbox, IconButton, Input, Option, Select
} from "@material-tailwind/react";
import { CheckCircleIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";

export default function EditItemDialog({ isOpen, onClose, onSuccess, requestHeaders, itemId }) {

    const [itemToEdit, setItemToEdit] = useState({});
    const [deleteTimer, setDeleteTimer] = useState(null);
    const [submitError, setSubmitError] = useState('');
    const timerRef = useRef(null);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setItemToEdit(prev => ({ ...prev, [name]: value }));
    };

    // Fetch full item data when dialog opens
    useEffect(() => {
        if (isOpen && itemId) {
            const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
            axios.get(`${API_URL}/purchased_items/${itemId}`, { headers: requestHeaders })
                .then(response => {
                    setItemToEdit(response.data);
                    setSubmitError('');
                })
                .catch(error => {
                    console.error("Error fetching item data:", error);
                    setSubmitError('Unable to load item details.');
                });
        }
    }, [isOpen, itemId, requestHeaders]);

    // Auto-set dates based on status changes
    useEffect(() => {
        if (isOpen && itemToEdit?.status) {
            const now = new Date();
            const offset = now.getTimezoneOffset() * 60000;
            const localTime = new Date(now - offset);
            const formattedDate = localTime.toISOString().slice(0, 16);

            if (itemToEdit.status === "SOLD" && !itemToEdit.saleDate) {
                setItemToEdit(prev => ({ ...prev, saleDate: formattedDate }));
            } else if (itemToEdit.status !== "SOLD" && itemToEdit.saleDate) {
                setItemToEdit(prev => ({ ...prev, saleDate: '' }));
            }

            if ((itemToEdit.status === "IN_STOCK" || itemToEdit.status === "IN_STOCK_PERSONAL") && !itemToEdit.availabilityDate) {
                setItemToEdit(prev => ({ ...prev, availabilityDate: formattedDate }));
            } else if (itemToEdit.status !== "IN_STOCK" && itemToEdit.status !== "IN_STOCK_PERSONAL" && itemToEdit.status !== "SOLD" && itemToEdit.availabilityDate) {
                setItemToEdit(prev => ({ ...prev, availabilityDate: '' }));
            }
        }
    }, [itemToEdit?.status, isOpen]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const reservedFor = itemToEdit.reservedFor?.trim() || '';
        if (itemToEdit.isReserved && !reservedFor) {
            setSubmitError('Enter who the item is reserved for.');
            return;
        }

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        try {
            setSubmitError('');
            await axios.put(
                `${API_URL}/purchased_items/edit/${itemToEdit.id}`,
                {
                    ...itemToEdit,
                    reservedFor: itemToEdit.isReserved ? reservedFor : null,
                },
                { headers: requestHeaders }
            );
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Error updating item:", err);
            setSubmitError(err.response?.data?.message || 'Unable to update item.');
        }
    };

    const handleReservedChange = (checked) => {
        setSubmitError('');
        setItemToEdit(prev => ({
            ...prev,
            isReserved: checked,
            ...(!checked ? { reservedFor: '' } : {}),
        }));
    };

    const handleDelete = async () => {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        if (!itemToEdit?.id) return;
        try {
            await axios.delete(`${API_URL}/purchased_items/delete/${itemToEdit.id}`, { headers: requestHeaders });
            onSuccess();
            onClose();
            setDeleteTimer(null);
        } catch (err) {
            console.error("Error deleting item:", err);
        }
    };

    const startDeleteCountdown = () => {
        if (deleteTimer !== null) {
            clearInterval(timerRef.current);
            setDeleteTimer(null);
            return;
        }
        let timeLeft = 5;
        setDeleteTimer(timeLeft);
        timerRef.current = setInterval(() => {
            timeLeft -= 1;
            setDeleteTimer(timeLeft);
            if (timeLeft === 0) {
                clearInterval(timerRef.current);
                handleDelete();
            }
        }, 1000);
    };

    const handleClose = () => {
        setDeleteTimer(null);
        setSubmitError('');
        if (timerRef.current) clearInterval(timerRef.current);
        onClose();
    };

    return (
        <Dialog size="l" open={isOpen} handler={handleClose}
                className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden">
            <DialogHeader className="relative m-0 block">
                <Typography variant="h4" color="blue-gray">Edit item</Typography>
                <Typography className="mt-1 font-normal text-gray-600">Update item you bought.</Typography>
                <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={handleClose}>
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Name</Typography>
                        <Input color="gray" size="lg" placeholder="eg. Naruto" name="title" value={itemToEdit?.title || ''} onChange={handleInput}
                            className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Author</Typography>
                        <Input color="gray" size="lg" placeholder="eg. Akira Toriyama" name="author" value={itemToEdit.author || ''} onChange={handleInput}
                            className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Condition</Typography>
                            <Input color="gray" size="lg" placeholder="eg. Very Good" name="itemCondition" value={itemToEdit.itemCondition || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Format type</Typography>
                            <Input color="gray" size="lg" placeholder="eg. Paperback" name="formatType" value={itemToEdit.formatType || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Price (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="price" value={itemToEdit.price ?? ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Price Fees (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="priceFees" value={itemToEdit.priceFees ?? ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Shipping Fees (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="shippingFees" value={itemToEdit.shippingFees ?? ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Parcel Forwarding Fees (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="parcelForwardingFees" value={itemToEdit.parcelForwardingFees ?? ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Selling Price (RSD)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="sellingPriceInRsd" value={itemToEdit.sellingPriceInRsd ?? ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Expected Selling Price (RSD)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="expectedSellingPriceInRsd" value={itemToEdit.expectedSellingPriceInRsd ?? ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Status</Typography>
                            <Select name="status"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'status', value } })}
                                value={itemToEdit.status || ''}>
                                <Option value="DISPATCHED">Dispatched</Option>
                                <Option value="IN_STOCK">In stock</Option>
                                <Option value="SOLD">Sold</Option>
                                <Option value="CANCELLED">Cancelled</Option>
                                <Option value="GIVEAWAY">Giveaway</Option>
                                <Option value="IN_STOCK_PERSONAL">In Stock Personal</Option>
                                <Option value="DISPATCHED_PERSONAL">Dispatched Personal</Option>
                            </Select>
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Purchased date</Typography>
                            <Input type="datetime-local" color="gray" size="lg" name="purchasedDate" value={itemToEdit.purchasedDate || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Availability date</Typography>
                            <Input type="datetime-local" color="gray" size="lg" name="availabilityDate" value={itemToEdit.availabilityDate || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Sale date</Typography>
                            <Input type="datetime-local" color="gray" size="lg" name="saleDate" value={itemToEdit.saleDate || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Payment Type</Typography>
                            <Select name="paymentType"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'paymentType', value } })}
                                value={itemToEdit.paymentType || ''}>
                                <Option value="IN_PERSON">In person</Option>
                                <Option value="DIRECT_PAYMENT_N">Direct Payment N</Option>
                                <Option value="DIRECT_PAYMENT_S">Direct Payment S</Option>
                                <Option value="DIRECT_PAYMENT_B">Direct Payment B</Option>
                                <Option value="DIRECT_PAYMENT_NA">Direct Payment NA</Option>
                                <Option value="DEXPRESS">DExpress</Option>
                                <Option value="POSTEXPRESS">PostExpress</Option>
                            </Select>
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Free space</Typography>
                            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <Typography variant="small" className="text-gray-600 dark:text-gray-400 italic">This space is reserved.</Typography>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Yugotransport Internal Number</Typography>
                            <Input color="gray" size="lg" placeholder="eg. A123B" name="yugotransportInternalNumber" value={itemToEdit.yugotransportInternalNumber || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Yugotransport Order Number</Typography>
                            <Input color="gray" size="lg" placeholder="eg. 123" name="yugotransportOrderNumber" value={itemToEdit.yugotransportOrderNumber || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">WOB Order Number</Typography>
                            <Input color="gray" size="lg" placeholder="eg. A123B" name="wobOrderNumber" value={itemToEdit.wobOrderNumber || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">RoyalMail Order Number</Typography>
                            <Input color="gray" size="lg" placeholder="eg. A123B" name="royalMailOrderNumber" value={itemToEdit.royalMailOrderNumber || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Image URL</Typography>
                            <Input color="gray" size="lg" placeholder="eg. https://..." name="imageUrl" value={itemToEdit.imageUrl || ''} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Type</Typography>
                            <Select name="type"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'type', value } })}
                                value={itemToEdit.type || ''}>
                                <Option value="BOOK">Book</Option>
                                <Option value="GAME">Game</Option>
                            </Select>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Is Damaged</Typography>
                            <Select name="isDamaged"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'isDamaged', value: value === 'true' } })}
                                value={itemToEdit.isDamaged?.toString() || 'false'}>
                                <Option value="true">Yes</Option>
                                <Option value="false">No</Option>
                            </Select>
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Reservation</Typography>
                            <div className={`rounded-lg border p-2 transition-colors ${
                                itemToEdit.isReserved
                                    ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/20'
                                    : 'border-blue-gray-200/90 bg-white dark:border-gray-700 dark:bg-dark-850'
                            }`}>
                                <Checkbox
                                    label={
                                        <Typography className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                            Reserved
                                        </Typography>
                                    }
                                    color="amber"
                                    ripple={false}
                                    checked={Boolean(itemToEdit.isReserved)}
                                    onChange={(event) => handleReservedChange(event.target.checked)}
                                    className="h-5 w-5 border-gray-300 bg-white hover:before:opacity-0 checked:border-amber-600 checked:bg-amber-600 dark:border-gray-600 dark:bg-dark-900 dark:checked:border-amber-500 dark:checked:bg-amber-500"
                                    containerProps={{ className: "p-1" }}
                                    labelProps={{ className: "flex items-center" }}
                                    crossOrigin={undefined}
                                />
                                {itemToEdit.isReserved && (
                                    <div className="mt-2">
                                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">
                                            Reserved for
                                        </Typography>
                                        <Input
                                            color="amber"
                                            size="lg"
                                            placeholder="eg. Marko - KP @marko87"
                                            name="reservedFor"
                                            value={itemToEdit.reservedFor || ''}
                                            onChange={handleInput}
                                            maxLength={255}
                                            required
                                            autoFocus
                                            className="placeholder:opacity-100 focus:!border-t-amber-700 dark:bg-dark-850"
                                            containerProps={{ className: "!min-w-full" }}
                                            labelProps={{ className: "hidden" }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="flex items-center justify-between gap-2">
                    <Button onClick={startDeleteCountdown}
                        className={deleteTimer !== null ? "mv-warning-button" : "mv-danger-button"}>
                        <TrashIcon className="h-4 w-4" />
                        {deleteTimer !== null ? `Abandon (${deleteTimer})` : "Delete"}
                    </Button>
                    <div className="flex flex-col items-end gap-2">
                        {submitError && (
                            <Typography variant="small" color="red" className="max-w-xs text-right font-medium">
                                {submitError}
                            </Typography>
                        )}
                        <Button type="submit" className="mv-primary-button">
                            <CheckCircleIcon className="h-4 w-4" />
                            Save
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </Dialog>
    );
}
